// Send Signup Link email typeahead — the input on
// /admin/portal-accounts/send-signup-link's Email field. A single unified
// contact search across Person/LegalEntity/Broker/Vendor/ExternalRecipient
// so staff can pick an existing contact instead of retyping. UX modelled on
// components/portfolioAdmin/loanDetail/parties/CounterpartyPicker.tsx
// (250ms debounce, >=2 chars, ARIA combobox, keyboard nav). Page wiring
// (WS3) lives in PortalSignupLinkPage.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/design-system/components/ui";
import {
  portalAccountsApi,
  type PortalContactSearchResultDto,
  type PortalContactSourceType,
} from "@/api/portalAccounts";
import { portalContactSearchKeys } from "@/hooks/portal/portalContactSearchKeys";

interface Props {
  /** Current email value. */
  value: string;
  /** Mirrors a raw <input onChange>. */
  onEmailChange: (email: string) => void;
  /** Fires when staff pick a typeahead row. */
  onSelectContact: (result: PortalContactSearchResultDto) => void;
  /** Fires on input blur (used by WS3 for the block-on-existing check). */
  onEmailBlur?: (email: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** For the parent FormField's label `htmlFor`. */
  inputId?: string;
  placeholder?: string;
}

const LISTBOX_ID = "portal-contact-search-listbox";

const SOURCE_LABEL: Record<PortalContactSourceType, string> = {
  Person: "Person",
  LegalEntity: "Entity",
  Broker: "Broker",
  Vendor: "Vendor",
  ExternalRecipient: "Recipient",
};

const SOURCE_PRIORITY: PortalContactSourceType[] = [
  "Broker",
  "Vendor",
  "Person",
  "LegalEntity",
  "ExternalRecipient",
];

function pickPrimarySource(
  sources: PortalContactSearchResultDto["sources"],
): { type: PortalContactSourceType; extra: number } | null {
  if (sources.length === 0) return null;
  const present = new Set(sources.map((s) => s.sourceType));
  for (const t of SOURCE_PRIORITY) {
    if (present.has(t)) {
      return { type: t, extra: sources.length - 1 };
    }
  }
  // Fallback: take the first row as-is.
  const first = sources[0];
  return { type: first.sourceType, extra: sources.length - 1 };
}

function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function EmailContactTypeahead({
  value,
  onEmailChange,
  onSelectContact,
  onEmailBlur,
  disabled,
  invalid,
  inputId,
  placeholder,
}: Props) {
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounced = useDebounced(value);
  const trimmed = debounced.trim();
  const enabled = trimmed.length >= 2;
  const open = focused && enabled;

  const results = useQuery<PortalContactSearchResultDto[]>({
    queryKey: portalContactSearchKeys.search(trimmed),
    queryFn: () => portalAccountsApi.searchContacts(trimmed),
    enabled,
  });
  const options = useMemo(() => results.data ?? [], [results.data]);

  useEffect(() => setActiveIndex(-1), [trimmed]);

  useEffect(
    () => () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    },
    [],
  );

  const select = (r: PortalContactSearchResultDto) => {
    onSelectContact(r);
    setFocused(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setFocused(false);
      return;
    }
    if (options.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < options.length) {
        e.preventDefault();
        select(options[activeIndex]);
      }
    }
  };

  return (
    <div className="relative">
      <Input
        id={inputId}
        type="email"
        value={value}
        onChange={(e) => onEmailChange(e.target.value)}
        onFocus={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          setFocused(true);
        }}
        onBlur={() => {
          // Delay close so a mousedown on a result row registers first.
          blurTimer.current = setTimeout(() => setFocused(false), 100);
          onEmailBlur?.(value);
        }}
        onKeyDown={onKeyDown}
        disabled={disabled}
        invalid={invalid}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `portal-contact-option-${activeIndex}` : undefined
        }
        data-testid="portal-contact-search-input"
      />
      {value.trim().length > 0 && value.trim().length < 2 && (
        <p className="text-caption text-gray-mid mt-1 m-0">
          Type at least 2 characters to search.
        </p>
      )}
      {open && (
        <ul
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Contact search results"
          className="absolute left-0 right-0 z-10 m-0 mt-space-1 p-0 list-none max-h-56 overflow-y-auto rounded-md border border-border-light bg-white-pure"
          data-testid="portal-contact-search-results"
        >
          {results.isLoading && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid italic">
              Searching…
            </li>
          )}
          {results.isError && (
            <li className="px-space-2 py-space-1 text-small text-status-error">
              Search failed. Try again.
            </li>
          )}
          {!results.isLoading && !results.isError && options.length === 0 && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid">
              No contacts match.
            </li>
          )}
          {options.map((r, i) => {
            const primary = pickPrimarySource(r.sources);
            const isActiveAccount = r.portalAccount?.isActive === true;
            return (
              <li key={`${r.email}-${i}`} role="presentation">
                <button
                  type="button"
                  id={`portal-contact-option-${i}`}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseDown={(e) => {
                    // Keep input focused so blur doesn't fire before select.
                    e.preventDefault();
                  }}
                  onClick={() => select(r)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`w-full flex items-baseline gap-space-2 px-space-2 py-space-1 text-left ${
                    i === activeIndex ? "bg-white-warm" : "bg-white-pure"
                  }`}
                  data-testid={`portal-contact-option-${i}`}
                >
                  <span className="text-small text-onyx font-semibold truncate">
                    {r.displayName}
                  </span>
                  <span className="text-caption text-gray-mid truncate">
                    {r.email}
                  </span>
                  {primary && (
                    <span className="inline-flex items-center px-space-1 py-[1px] rounded-full bg-white-warm text-caption text-gray-mid shrink-0">
                      {SOURCE_LABEL[primary.type]}
                      {primary.extra > 0 ? ` +${primary.extra}` : ""}
                    </span>
                  )}
                  {isActiveAccount && (
                    <span
                      className="inline-flex items-center px-space-1 py-[1px] rounded-full bg-status-warning/15 text-status-warning text-caption shrink-0 ml-auto"
                      data-testid="portal-contact-active-account-chip"
                    >
                      Active account
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
